<template>
    <CCard class="order-upsell">
        <CCardHeader>
            <strong>Допродажи в статусе заказа</strong>
        </CCardHeader>
        <CCardBody>
            <p v-if="isLoading" role="status">Загрузка настроек…</p>
            <div
                v-else-if="loadError"
                class="alert alert-danger"
                role="alert">
                {{ loadError }}
                <button
                    type="button"
                    class="btn btn-link"
                    @click="loadSettings">Повторить</button>
            </div>
            <form v-else-if="hasLoaded" @submit.prevent="saveSettings">
                <fieldset :disabled="isSaving">
                    <div class="custom-control custom-switch mb-4">
                        <input
                            id="order-upsell-enabled"
                            v-model="isEnabled"
                            type="checkbox"
                            class="custom-control-input"
                        />
                        <label class="custom-control-label" for="order-upsell-enabled">Показывать допродажи</label>
                    </div>

                    <div class="order-upsell__rules mb-4">
                        <div><strong>После заказа в ресторане</strong> — выбранные работающие магазины.</div>
                        <div><strong>После заказа в магазине</strong> — выбранный личный помощник, переход на общую страницу.</div>
                        <div><strong>После заказа у личного помощника</strong> — выбранные рестораны и магазины.</div>
                    </div>

                    <div
                        class="order-upsell__pools mb-4"
                        role="group"
                        aria-label="Тип исходного заказа">
                        <button
                            v-for="pool in pools"
                            :key="pool.key"
                            type="button"
                            class="btn"
                            :class="activePool === pool.key ? 'btn-primary' : 'btn-outline-primary'"
                            :aria-pressed="activePool === pool.key"
                            :data-testid="pool.key"
                            @click="activePool = pool.key"
                        >{{ pool.title }} ({{ partnerPools[pool.key].length }})</button>
                    </div>
                    <h5>{{ activePoolTitle }}</h5>
                    <p class="text-muted">
                        {{ poolHint }} Перетащите партнёров за маркер
                        или используйте стрелки, чтобы изменить порядок показа.
                    </p>

                    <div
                        v-if="hasDetailsError"
                        class="alert alert-warning"
                        role="alert">
                        Не удалось загрузить часть названий. Ваш выбор сохранён.
                        <button
                            type="button"
                            class="btn btn-link"
                            @click="loadSelectedDetails">Повторить</button>
                    </div>
                    <p
                        v-if="isDetailsLoading"
                        class="text-muted"
                        role="status">Загрузка выбранных партнёров…</p>
                    <div
                        v-if="!partners.length"
                        class="order-upsell__empty mb-3"
                        data-testid="empty-pool">
                        Партнёры пока не выбраны. Для этого типа заказа шильдик показываться не будет.
                    </div>
                    <Draggable
                        v-model="partners"
                        tag="ol"
                        class="order-upsell__selected"
                        handle=".order-upsell__drag-handle"
                        :disabled="isSaving"
                    >
                        <li
                            v-for="(partner, index) in partners"
                            :key="getPartnerKey(partner)"
                            class="order-upsell__row"
                            data-testid="selected-partner">
                            <span class="order-upsell__drag-handle" aria-hidden="true">⠿</span>
                            <span class="text-muted">{{ index + 1 }}.</span>
                            <div class="order-upsell__partner">
                                <strong>{{ getPartnerTitle(partner) }}</strong>
                                <div class="small text-muted">{{ getPartnerTypeTitle(partner) }} · № {{ partner.id }}</div>
                                <span v-if="getPartnerStatus(partner)" class="badge badge-warning">{{ getPartnerStatus(partner) }}</span>
                            </div>
                            <div class="order-upsell__actions">
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-dark"
                                    :disabled="index === 0"
                                    :aria-label="'Поднять ' + getPartnerTitle(partner)"
                                    data-testid="move-up"
                                    @click="movePartner(index, -1)"
                                >↑</button>
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-dark"
                                    :disabled="index === partners.length - 1"
                                    :aria-label="'Опустить ' + getPartnerTitle(partner)"
                                    data-testid="move-down"
                                    @click="movePartner(index, 1)"
                                >↓</button>
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-danger"
                                    data-testid="remove-partner"
                                    @click="removePartner(index)">Удалить</button>
                            </div>
                        </li>
                    </Draggable>
                    <p class="small text-muted">Выбрано {{ partners.length }} из {{ partnersLimit }}. Выключенные и архивные партнёры не показываются в приложении.</p>
                    <p
                        v-if="hasMissingPartners"
                        class="text-danger"
                        role="alert">Некоторые партнёры удалены. Уберите их из списка перед сохранением.</p>

                    <p
                        v-if="hasInvalidPartners"
                        class="text-danger"
                        role="alert">В подборках остались партнёры недопустимого типа. Удалите их перед сохранением.</p>
                    <div class="order-upsell__search mt-4">
                        <h5>Добавить партнёра</h5>
                        <div class="form-row">
                            <div class="form-group col-md-4">
                                <label for="order-upsell-type">Категория</label>
                                <select
                                    id="order-upsell-type"
                                    v-model="partnerType"
                                    class="form-control">
                                    <option
                                        v-for="type in partnerTypes"
                                        :key="type.value"
                                        :value="type.value">{{ type.title }}</option>
                                </select>
                            </div>
                            <div class="form-group col-md-8">
                                <label for="order-upsell-search">Название партнёра</label>
                                <input
                                    id="order-upsell-search"
                                    v-model="searchQuery"
                                    type="search"
                                    class="form-control"
                                    maxlength="100"
                                    placeholder="Поиск по названию"
                                    @keydown.enter.prevent />
                            </div>
                        </div>
                        <div
                            v-if="optionsError"
                            class="alert alert-danger"
                            role="alert">
                            {{ optionsError }}
                            <button
                                type="button"
                                class="btn btn-link"
                                @click="loadPartnerOptions(failedPage)">Повторить</button>
                        </div>
                        <ul class="order-upsell__options">
                            <li
                                v-for="partner in options"
                                :key="getPartnerKey(partner)"
                                class="order-upsell__row"
                                data-testid="partner-option">
                                <div class="order-upsell__partner">
                                    <strong>{{ partner.title }}</strong>
                                    <div class="small text-muted">{{ getPartnerTypeTitle(partner) }} · № {{ partner.id }}</div>
                                    <span v-if="getPartnerStatus(partner)" class="badge badge-warning">{{ getPartnerStatus(partner) }}</span>
                                </div>
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-primary"
                                    :disabled="isPartnerSelected(partner) || partners.length >= partnersLimit"
                                    data-testid="add-partner"
                                    @click="addPartner(partner)"
                                >{{ isPartnerSelected(partner) ? 'Добавлен' : 'Добавить' }}</button>
                            </li>
                        </ul>
                        <p v-if="isOptionsLoading" role="status">Загрузка партнёров…</p>
                        <p v-else-if="!options.length && !optionsError" class="text-muted">Партнёры не найдены.</p>
                        <button
                            v-if="hasMoreOptions && !optionsError"
                            type="button"
                            class="btn btn-outline-secondary"
                            :disabled="isOptionsLoading"
                            @click="loadPartnerOptions(optionsPage + 1)">Показать ещё</button>
                    </div>
                </fieldset>

                <div class="order-upsell__footer mt-4">
                    <p
                        v-if="saveError"
                        class="text-danger"
                        role="alert">{{ saveError }}</p>
                    <p
                        v-if="saveMessage && !hasChanges"
                        class="text-success"
                        role="status">{{ saveMessage }}</p>
                    <button
                        type="submit"
                        class="btn btn-primary"
                        :disabled="isSaving || !hasChanges || hasMissingPartners || hasInvalidPartners"
                        data-testid="save-settings">
                        {{ isSaving ? 'Сохранение…' : 'Сохранить' }}
                    </button>
                    <span v-if="hasChanges" class="text-muted ml-3">Есть несохранённые изменения</span>
                </div>
            </form>
        </CCardBody>
    </CCard>
</template>

<script>
    /* eslint vue/script-indent: off */
    // Legacy babel-eslint exposes token objects that crash this Vue 6 rule; retain the project's four-space indentation.
    import Vue from 'vue';
    import Draggable from 'vuedraggable';

    const PARTNERS_LIMIT = 100;
    const PARTNER_TYPES = [
        { value: 'restaurant', title: 'Рестораны' },
        { value: 'grocer', title: 'Магазины' },
        { value: 'entity', title: 'Личный помощник' },
    ];

    export default {
        name: 'OrderUpsellSettings',
        components: { Draggable },
        data() {
            return {
                isEnabled: false,
                partnerPools: { partners: [], restaurant_partners: [], grocer_partners: [] },
                activePool: 'partners',
                pools: [
                    { key: 'restaurant_partners', title: 'После ресторана' },
                    { key: 'grocer_partners', title: 'После магазина' },
                    { key: 'partners', title: 'После личного помощника' },
                ],
                partnerDetails: {},
                missingPartnerKeys: [],
                partnersLimit: PARTNERS_LIMIT,
                partnerType: 'restaurant',
                searchQuery: '',
                options: [],
                optionsPage: 0,
                hasMoreOptions: false,
                isLoading: true,
                hasLoaded: false,
                isSaving: false,
                isDetailsLoading: false,
                isOptionsLoading: false,
                loadError: '',
                hasDetailsError: false,
                optionsError: '',
                saveError: '',
                saveMessage: '',
                savedPayload: '',
                optionsRequestId: 0,
                searchTimerId: null,
                failedPage: 1,
                isDisposed: false,
            };
        },
        computed: {
            partners: {
                get() { return this.partnerPools[this.activePool]; },
                set(value) { this.$set(this.partnerPools, this.activePool, value); },
            },
            partnerTypes() {
                const allowed = this.activePool === 'partners' ? ['restaurant', 'grocer'] : this.activePool === 'restaurant_partners' ? ['grocer'] : ['entity'];
                return PARTNER_TYPES.filter(type => allowed.includes(type.value));
            },
            activePoolTitle() {
                return { partners: 'Партнёры после заказа у личного помощника', restaurant_partners: 'Партнёры после заказа у ресторана', grocer_partners: 'Партнёры после заказа у магазина' }[this.activePool];
            },
            poolHint() {
                return { partners: 'Добавьте рестораны и магазины.', restaurant_partners: 'Добавьте магазины. Показываются только работающие и доступные.', grocer_partners: 'Добавьте личного помощника. Шильдик ведёт на общую страницу ЛП.' }[this.activePool];
            },
            allSelectedPartners() { return Object.values(this.partnerPools).flat(); },
            hasInvalidPartners() {
                return Object.entries(this.partnerPools).some(([key, partners]) => partners.some(partner => !this.isPartnerAllowed(partner, key)));
            },
            hasChanges() {
                return this.hasLoaded && JSON.stringify(this.getPayload()) !== this.savedPayload;
            },
            hasMissingPartners() {
                return this.allSelectedPartners.some(partner => this.missingPartnerKeys.includes(this.getPartnerKey(partner)));
            },
        },
        watch: {
            activePool() {
                this.partnerType = this.partnerTypes[0].value;
                this.searchQuery = '';
                this.schedulePartnerSearch();
            },
            partnerType() { this.schedulePartnerSearch(); },
            searchQuery() { this.schedulePartnerSearch(); },
        },
        mounted() {
            this.loadSettings();
        },
        beforeDestroy() {
            this.isDisposed = true;
            this.optionsRequestId++;
            clearTimeout(this.searchTimerId);
        },
        methods: {
            getPayload() {
                const payload = { enabled: this.isEnabled };
                Object.entries(this.partnerPools).forEach(([key, partners]) => { payload[key] = partners.map(({ type, id }) => ({ type, id })); });
                return payload;
            },
            isPartnerAllowed(partner, pool = this.activePool) {
                if (pool === 'partners') return ['restaurant', 'grocer'].includes(partner.type);
                if (pool === 'restaurant_partners') return partner.type === 'grocer';
                const details = this.partnerDetails[this.getPartnerKey(partner)] || partner;
                return partner.type === 'entity' && (!details.entity_type_code || details.entity_type_code === 'assist');
            },
            getPartnerKey(partner) {
                return `${partner.type}:${partner.id}`;
            },
            getPartnerTitle(partner) {
                const details = this.partnerDetails[this.getPartnerKey(partner)];
                return (details && details.title) || `Партнёр № ${partner.id}`;
            },
            getPartnerTypeTitle(partner) {
                const details = this.partnerDetails[this.getPartnerKey(partner)] || partner;
                const type = PARTNER_TYPES.find(type => type.value === partner.type);
                return details.entity_type_title || (type && type.title) || partner.type;
            },
            getPartnerStatus(partner) {
                const key = this.getPartnerKey(partner);
                if (this.missingPartnerKeys.includes(key)) return 'Не найден';
                if (!this.isPartnerAllowed(partner)) return 'Недопустимый тип для этой подборки';
                const details = this.partnerDetails[key];
                if (!details) return 'Название не загружено';
                if (details.archive) return 'В архиве';
                if (!details.enabled) return 'Выключен';
                return '';
            },
            isPartnerSelected(partner) {
                return this.partners.some(item => this.getPartnerKey(item) === this.getPartnerKey(partner));
            },
            cachePartnerDetails(items) {
                items.forEach(item => this.$set(this.partnerDetails, this.getPartnerKey(item), item));
            },
            async loadSettings() {
                if (this.hasLoaded || this.isDisposed) return;
                this.isLoading = true;
                this.loadError = '';
                try {
                    const { data } = await Vue.axios.get('/order-upsell/settings');
                    if (this.isDisposed) return;
                    if (!data || typeof data.enabled !== 'boolean' || !Array.isArray(data.partners)) throw new Error('Invalid settings');
                    this.isEnabled = data.enabled;
                    for (const key of Object.keys(this.partnerPools)) {
                        if (data[key] !== undefined && !Array.isArray(data[key])) throw new Error('Invalid pool');
                        this.$set(this.partnerPools, key, (data[key] || []).map(({ type, id }) => ({ type, id })));
                    }
                    this.savedPayload = JSON.stringify(this.getPayload());
                    this.hasLoaded = true;
                    await Promise.all([this.loadSelectedDetails(), this.loadPartnerOptions()]);
                } catch (error) {
                    if (!this.isDisposed) this.loadError = 'Не удалось загрузить настройки. Попробуйте ещё раз.';
                } finally {
                    if (!this.isDisposed) this.isLoading = false;
                }
            },
            async loadSelectedDetails() {
                if (this.isDetailsLoading || this.isDisposed) return;
                this.isDetailsLoading = true;
                this.hasDetailsError = false;
                const groups = [];
                PARTNER_TYPES.forEach(({ value: type }) => {
                    const ids = [...new Set(this.allSelectedPartners.filter(partner => partner.type === type).map(partner => partner.id))];
                    for (let offset = 0; offset < ids.length; offset += PARTNERS_LIMIT) groups.push({ type, ids: ids.slice(offset, offset + PARTNERS_LIMIT) });
                });
                const results = await Promise.allSettled(groups.map(group => Vue.axios.get('/order-upsell/partners', {
                    params: { type: group.type, ids: group.ids.join(','), page_size: PARTNERS_LIMIT },
                })));
                if (this.isDisposed) return;
                results.forEach((result, index) => {
                    if (result.status !== 'fulfilled' || !result.value.data || !Array.isArray(result.value.data.items)) {
                        this.hasDetailsError = true;
                        return;
                    }
                    const items = result.value.data.items;
                    const group = groups[index];
                    this.cachePartnerDetails(items);
                    const requestedKeys = group.ids.map(id => `${group.type}:${id}`);
                    const foundKeys = items.map(this.getPartnerKey);
                    this.missingPartnerKeys = this.missingPartnerKeys.filter(key => !requestedKeys.includes(key))
                        .concat(requestedKeys.filter(key => !foundKeys.includes(key)));
                });
                this.isDetailsLoading = false;
            },
            schedulePartnerSearch() {
                clearTimeout(this.searchTimerId);
                this.optionsRequestId++;
                this.options = [];
                this.optionsPage = 0;
                this.hasMoreOptions = false;
                this.optionsError = '';
                this.isOptionsLoading = true;
                this.searchTimerId = setTimeout(() => this.loadPartnerOptions(), 300);
            },
            async loadPartnerOptions(page = 1) {
                if (this.isDisposed) return;
                const requestId = ++this.optionsRequestId;
                this.isOptionsLoading = true;
                this.optionsError = '';
                try {
                    const { data } = await Vue.axios.get('/order-upsell/partners', {
                        params: { type: this.partnerType, q: this.searchQuery.trim(), page, page_size: 20, ...(this.activePool === 'grocer_partners' ? { entity_type_code: 'assist' } : {}) },
                    });
                    if (requestId !== this.optionsRequestId || this.isDisposed) return;
                    if (!data || !Array.isArray(data.items)) throw new Error('Invalid partners');
                    this.cachePartnerDetails(data.items);
                    const combined = page === 1 ? data.items : [...this.options, ...data.items];
                    this.options = [...new Map(combined.map(item => [this.getPartnerKey(item), item])).values()];
                    this.optionsPage = page;
                    this.hasMoreOptions = !!data.has_more;
                } catch (error) {
                    if (requestId !== this.optionsRequestId || this.isDisposed) return;
                    this.failedPage = page;
                    this.optionsError = 'Не удалось загрузить партнёров.';
                } finally {
                    if (requestId === this.optionsRequestId && !this.isDisposed) this.isOptionsLoading = false;
                }
            },
            addPartner(partner) {
                if (!this.isPartnerAllowed(partner) || this.isSaving || this.partners.length >= PARTNERS_LIMIT || this.isPartnerSelected(partner)) return;
                this.partners.push({ type: partner.type, id: partner.id });
                this.saveMessage = '';
            },
            removePartner(index) {
                if (this.isSaving) return;
                this.partners.splice(index, 1);
                this.saveMessage = '';
            },
            movePartner(index, direction) {
                const target = index + direction;
                if (this.isSaving || target < 0 || target >= this.partners.length) return;
                const partner = this.partners.splice(index, 1)[0];
                this.partners.splice(target, 0, partner);
                this.saveMessage = '';
            },
            async saveSettings() {
                if (!this.hasLoaded || this.isSaving || !this.hasChanges || this.hasMissingPartners || this.hasInvalidPartners) return;
                this.isSaving = true;
                this.saveError = '';
                this.saveMessage = '';
                const payload = this.getPayload();
                try {
                    await Vue.axios.put('/order-upsell/settings', payload);
                    if (this.isDisposed) return;
                    this.savedPayload = JSON.stringify(payload);
                    this.saveMessage = 'Настройки допродаж сохранены.';
                } catch (error) {
                    if (this.isDisposed) return;
                    const data = error.response && error.response.data;
                    this.saveError = (data && (data.message || data.error)) || 'Не удалось сохранить настройки. Попробуйте ещё раз.';
                } finally {
                    if (!this.isDisposed) this.isSaving = false;
                }
            },
        },
    };
</script>

<style scoped>
    .order-upsell { max-width: 1100px; }
    .order-upsell__pools { display: flex; gap: 12px; flex-wrap: wrap; }
    .order-upsell fieldset { border: 0; padding: 0; margin: 0; min-width: 0; }
    .order-upsell__rules, .order-upsell__empty { background: #f5f6f8; border-radius: 4px; padding: 16px; }
    .order-upsell__rules > div + div { margin-top: 8px; }
    .order-upsell__selected, .order-upsell__options { list-style: none; margin: 0; padding: 0; }
    .order-upsell__row { display: flex; align-items: center; gap: 12px; padding: 12px 0; border-bottom: 1px solid #e8e8ed; }
    .order-upsell__partner { flex: 1; min-width: 0; overflow-wrap: anywhere; }
    .order-upsell__drag-handle { cursor: grab; padding: 8px; font-size: 24px; color: #768192; }
    .order-upsell__actions { display: flex; gap: 6px; }
    .order-upsell__search { padding-top: 20px; border-top: 1px solid #d8dbe0; }
    .order-upsell__footer { padding-top: 20px; border-top: 1px solid #d8dbe0; }
    @media (max-width: 575px) {
        .order-upsell__selected .order-upsell__row { flex-wrap: wrap; }
        .order-upsell__actions { margin-left: auto; }
    }
</style>
